import React, { useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import Text from 'components/base/Text';
import Icon from 'components/base/Icon';
import { Flex } from 'components/base/layout-components';
import { getColor } from 'common/constants/_colors';
import { PM_PROVIDERS, ARTICLE_IDS } from 'common/constants/constants';
import { useDispatch, useSelector } from 'react-redux';
import * as LighthouseService from 'common/services/lighthouse.service';
import { pmIntegrationSelector } from 'state/selectors/sensors-authorizations';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import { pmFiltersBuilder } from 'components/FilterMenus/pm.filters';
import FilterMenusV2 from 'components/FilterMenus/FilterMenusV2';
import { FILTER_TYPES } from 'common/constants/filters';
import { Stack } from '@mui/material';
import { Body2 } from '@linearb/linui-common/typography';
import { lightColorPalette } from '@linearb/linui-common';
import { StyledSectionTitle } from './CreateEditProjectModal.style';
import { toggleModal } from 'state/actions';
import useToast from 'components/hooks/useToast';
import SnackBarMessage from 'components/Message';
import { LBButton } from '@linearb/buzz';

const StyledIcon = styled(Icon)`
  :hover {
    cursor: pointer;
    color: ${getColor('chambray')} !important;
  }
`;

export const Header = ({ isEdit, projectName }) => {
  return (
    <Flex items='center' mb='big'>
      <Icon name='projects' size='medium' color='osloGray' isThin />
      <Text size='x-large' ml='1rem'>
        {isEdit ? `Edit Project: ${projectName}` : 'Create new project'}
      </Text>
    </Flex>
  );
};

export const Scopes = ({
  selectedFilters,
  setSelectedFilters,
  labelComponent = null,
  curProjectName = '',
  isEdit,
  onDeleteFilter,
  customFieldsMapper
}) => {
  const pmSensor = useSelector(pmIntegrationSelector);
  const isAzureBoards = pmSensor?.sensor_id === PM_PROVIDERS.AZURE_BOARDS;
  const { getSettingOrDefinitionValue } = useSettingsService();

  const pdtForShortcutEnabledFF = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.pdtForShortcutEnabled
  });

  const isPdtTreeHierarchyEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isPdtTreeHierarchyEnabled
  });

  const menuItemsV2 = pmFiltersBuilder(
    {
      [PM_PROVIDERS.JIRA]: [
        !isPdtTreeHierarchyEnabled && FILTER_TYPES.EPIC,
        FILTER_TYPES.LABEL,
        isPdtTreeHierarchyEnabled && FILTER_TYPES.DYNAMIC_ISSUES,
        FILTER_TYPES.FIELD,
        FILTER_TYPES.FIX_VERSION
      ].filter(Boolean),
      [PM_PROVIDERS.CLUBHOUSE]: [FILTER_TYPES.PROJECTS, FILTER_TYPES.TEAMS],
      [PM_PROVIDERS.AZURE_BOARDS]: [
        isPdtTreeHierarchyEnabled
          ? FILTER_TYPES.DYNAMIC_ISSUES
          : FILTER_TYPES.EPIC,
        FILTER_TYPES.LABEL,
        FILTER_TYPES.FIELD
      ].filter(Boolean)
    },
    getSettingOrDefinitionValue
  );

  const title = isAzureBoards ? 'Additional Filters' : 'Project Scope';

  const formattedFilters = _.omit(selectedFilters, [
    FILTER_TYPES.BOARD,
    FILTER_TYPES.AZURE_BOARDS_PROJECT_TEAM_AREAS
  ]);

  return (
    <>
      <Stack spacing={1}>
        <Stack spacing={1} direction={'row'}>
          <StyledSectionTitle>{title}</StyledSectionTitle>
          {!isAzureBoards && (
            <StyledIcon
              name='helpicon'
              size='smaller'
              color='nepal'
              onClick={() =>
                LighthouseService.navigateArticle(
                  ARTICLE_IDS.SET_JIRA_BOARD_PER_TEAM
                )
              }
            />
          )}
        </Stack>
        {pmSensor.sensor_id === PM_PROVIDERS.CLUBHOUSE &&
        pdtForShortcutEnabledFF ? (
          <Body2 color={lightColorPalette.lbTextSubdued}>
            Add one or more filters to identify the project (e.g. Project, team,
            epic, label etc) :
          </Body2>
        ) : (
          <Body2 color={lightColorPalette.lbTextSubdued}>
            Add one or more filters to identify the project (e.g. Jira board,
            epic, label etc) :
          </Body2>
        )}
      </Stack>
      <Stack marginTop={1.5}>
        <FilterMenusV2
          customFieldsMapper={customFieldsMapper}
          customFieldById
          menuItems={menuItemsV2}
          selectedFilters={formattedFilters}
          setSelectedFilters={updatedFilters =>
            setSelectedFilters({ ...selectedFilters, ...updatedFilters })
          }
          onDeleteFilter={onDeleteFilter}
          documentIdPrefix='select-filters'
        />
      </Stack>
    </>
  );
};

export const Actions = ({ handleSave, isValid }) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);

  /** Handles project save operation and error handling */
  const onSaveProject = async () => {
    try {
      setIsSaving(true);
      await handleSave();
    } catch (e) {
      dispatch(toggleModal());
      toast({
        message: <SnackBarMessage message={e.message} />,
        variant: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <Flex justify='flex-end'>
      <LBButton onClick={onSaveProject} disabled={!isValid || isSaving}>
        Save
      </LBButton>
    </Flex>
  );
};
